import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import { fetchStructuredRequestsPaginated } from "@/queries/requests/useStructuredRequestsPaginated";
import { fetchTracesSearchPaginated } from "@/queries/traces/fetchTracesSearchPaginated";

export type ObservabilitySetupMode = "requests" | "traces";

const POLL_INTERVAL_MS = 5000;

interface UseWaitForFirstRequestOrTraceArgs {
  mode: ObservabilitySetupMode;
  enabled: boolean;
  userToken: string;
  workspaceId: number;
}

export const useWaitForFirstRequestOrTrace = ({
  mode,
  enabled,
  userToken,
  workspaceId,
}: UseWaitForFirstRequestOrTraceArgs) => {
  const setupStartedAtRef = useRef(Date.now());
  const requestsQuery = useQuery({
    queryKey: ["onboarding", "first-request", userToken, workspaceId],
    queryFn: () =>
      fetchStructuredRequestsPaginated({
        userToken,
        workspaceId,
        page: 1,
        pageSize: 1,
        searchParams: {
          sortBy: { id: "request_start_time", desc: true },
          includeCount: false,
        },
      }),
    enabled: enabled && mode === "requests" && Boolean(userToken) && workspaceId > 0,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 0,
  });

  const tracesQuery = useQuery({
    queryKey: ["onboarding", "first-trace", userToken, workspaceId],
    queryFn: () =>
      fetchTracesSearchPaginated({
        userToken,
        workspaceId,
        page: 1,
        pageSize: 1,
        searchParams: {
          sortField: "start",
          sortOrder: "desc",
        },
      }),
    enabled: enabled && mode === "traces" && Boolean(userToken) && workspaceId > 0,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 0,
  });

  const requestItem = requestsQuery.data?.items?.[0];
  const traceItem = tracesQuery.data?.items?.[0];
  const requestReceived = Boolean(
    requestItem && Date.parse(requestItem.request_start_time) >= setupStartedAtRef.current,
  );
  const traceReceived = Boolean(
    traceItem?.start && Date.parse(traceItem.start) >= setupStartedAtRef.current,
  );

  if (mode === "requests") {
    return {
      received: requestReceived,
      itemId: requestReceived ? (requestItem?.id ?? null) : null,
      statusLabel: requestReceived ? "Request received" : "Waiting for your first request",
    };
  }

  return {
    received: traceReceived,
    itemId: traceReceived ? (traceItem?.trace_id ?? null) : null,
    statusLabel: traceReceived ? "Trace received" : "Waiting for your first trace",
  };
};
